import { useState } from 'react'

import { AttendanceCustomTimeRangeDayEnum } from 'generated/resources/datatable.graphql'
import {
  CreateCustomTimeRangeDocument,
  CreateCustomTimeRangeMutationVariables,
  SettingPageCustomTimeRangeFragmentDoc,
  UpdateCustomTimeRangeMutationVariables,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useUpdateCustomTimeRange } from 'modules/attendance/hooks/useUpdateCustomTimeRange'
import { timeRangeCategoryTypeEnum } from 'modules/attendance/lib/enums'
import { WizardData } from 'modules/attendance/lib/forms/utils/CustomTimeRangesFormData'
import useHandleReumbursableMappings from 'modules/attendance/lib/forms/utils/useHandleReimbursableMappings'
import { minutesToCents } from 'modules/attendance/lib/timeConverter'
import useUpsertReimbursablePayrollConcept from 'modules/compensations/hooks/useUpsertReimbursablePayrollConcept'

interface Props {
  settingId: string
  dayType?: AttendanceCustomTimeRangeDayEnum
}

const createCustomTimeRanges = (data: WizardData) => {
  const defaultDate = '2000-01-01'
  const startAt = data.startTime
    ? moment.utc(`${defaultDate} ${data.startTime}`).toISOString()
    : undefined
  const endAt = data.endTime
    ? moment.utc(`${defaultDate} ${data.endTime}`).toISOString()
    : undefined

  const commonFields = {
    timeSettingsCustomTimeRangeCategoryId: data.timeSettingsCustomTimeRangeCategoryId ?? '',
    poolType: data.poolType,
    startTime: startAt,
    endTime: endAt,
    attendanceEmployeesSettingId: data.attendanceEmployeesSettingId,
    equivalentMinutesInCents: minutesToCents(data.equivalentMinutes),
    rawMinutesInCents: minutesToCents(data.rawMinutes),
    ...(data.id ? { id: data.id } : {}),
  }

  if (data.day) {
    return data.day.map((selectedDay) => ({
      ...commonFields,
      day: selectedDay,
    }))
  } else {
    return [
      {
        ...commonFields,
        date: data.date,
      },
    ]
  }
}

export const useUpsertCustomTimeRanges = ({ settingId }: Props) => {
  const [create] = useMutation(CreateCustomTimeRangeDocument, {
    extractorFn: (data) => data.attendanceMutations.bulkCreateAttendanceCustomTimeRange,
    update: (cache, { data }) => {
      const newRanges =
        data?.attendanceMutations.bulkCreateAttendanceCustomTimeRange.customTimeRanges
      if (!newRanges) return

      cache.modify({
        id: `AttendanceEmployeesSetting:${settingId}`,
        fields: {
          customTimeRanges(existingRanges, { readField }) {
            const existingIds = new Set(
              existingRanges.map((item: { _ref: string }) => readField('id', item))
            )
            const newItems = newRanges.filter((item) => !existingIds.has(item.id))

            const newCustomTimeRanges = newItems.map((item) =>
              cache.writeFragment({
                data: item,
                fragment: SettingPageCustomTimeRangeFragmentDoc,
                fragmentName: 'SettingPageCustomTimeRange',
              })
            )

            return [...existingRanges, ...newCustomTimeRanges]
          },
        },
      })
    },
  })
  const { update } = useUpdateCustomTimeRange()
  const { manageMappings } = useHandleReumbursableMappings()
  const { upsert: upsertCompensationMapping } = useUpsertReimbursablePayrollConcept()

  const [errors, setErrors] = useState({})

  const handleUpsertCompensationMapping = async (data: WizardData) => {
    if (!data.payrollConceptId) return

    const reimbursableId =
      data.timeSettingsCustomTimeRangeCategoryId != null
        ? String(data.timeSettingsCustomTimeRangeCategoryId)
        : undefined

    if (!reimbursableId) return

    const existingCompensationMapping = data.payrollMappingsCompensations?.find(
      (mapping) => mapping.reimbursableId === reimbursableId
    )

    const result = await upsertCompensationMapping({
      id: existingCompensationMapping?.id ? String(existingCompensationMapping.id) : undefined,
      payrollConceptId: data.payrollConceptId,
      reimbursableId,
      reimbursableType: timeRangeCategoryTypeEnum.TimeSettingsCustomTimeRangeCategory,
    })

    return handleResult(result, setErrors)
  }

  const updateOnSubmit = async (data: UpdateCustomTimeRangeMutationVariables) => {
    const result = await update(data)
    const handled = handleResult(result, ({ day }) => {
      setErrors({ day })
    })

    return handled.success
  }

  const createOnSubmit = async (data: CreateCustomTimeRangeMutationVariables) => {
    const result = await create({
      variables: data,
    })
    const handled = handleResult(result, ({ day }) => {
      setErrors({ day })
    })

    return handled.success
  }

  const onSubmit = async (data: WizardData) => {
    const customTimeRanges = createCustomTimeRanges(data)
    const payload = {
      attendanceEmployeesSettingId: customTimeRanges[0]?.attendanceEmployeesSettingId ?? settingId,
      customTimeRanges,
    }

    const success = data.id ? await updateOnSubmit(payload) : await createOnSubmit(payload)

    if (success) {
      const mappings = data.formMappingsTaxonomies ?? []

      //TODO: Remove this once the new compensation module is fully released
      manageMappings(setErrors, mappings)

      handleUpsertCompensationMapping(data)

      track(trackingEvents.timeTracking.addTimeBlock, {
        day: data.day,
        time_block_type: data.slotSelected === 'full-day' ? 'full_day' : 'time_slot',
        start_time: data.startTime,
        end_time: data.endTime,
        pool_type: data.poolType,
      })

      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    }

    return success
  }

  return { onSubmit, errors }
}
